import React, { useState, useCallback, useEffect } from 'react';
import { Popover } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { ITooltip, sendTooltipAnalytics } from './tooltip';
import { useLocation } from '@gatsbyjs/reach-router';
import PopoverContent from './popover-content';
import useAnalytics from '../../../../hooks/useAnalytics';

//Custom hooks that Contains propper behaviour. This way all of this logic is in the same place.
const offsetDefault: [number, number] = [0, 10]; //NOSONAR
const offsetTop: [number, number] = [0, 10]; //NOSONAR
const offsetRight: [number, number] = [0, 20]; //NOSONAR
const usePopperCustomConfig = (
	referenceElement: HTMLElement | null | undefined,
	popperElement: HTMLElement | null | undefined
) => {
	const [offsetPopper, setOffsetPopper] =
		useState<[number, number]>(offsetDefault);

	const { styles, attributes, state, update } = usePopper(
		referenceElement,
		popperElement,
		{
			placement: 'right',

			modifiers: [
				{
					name: 'flip',
					options: {
						fallbackPlacements: ['bottom', 'top'],
						rootBoundary: 'viewport',
					},
				},
				{
					name: 'offset',
					options: {
						offset: offsetPopper,
					},
				},
				{
					name: 'preventOverflow',
					options: {
						mainAxis: true,
					},
				},
			],
		}
	);

	useEffect(() => {
		const updateOffset = ({ offset }: { offset: [number, number] }) => {
			setOffsetPopper(offset);
			if (update) {
				update();
			}
		};

		switch (state?.placement) {
			case 'bottom':
			case 'top':
				updateOffset({ offset: offsetTop });
				break;
			case 'right':
				updateOffset({ offset: offsetRight });
				break;
			default:
				updateOffset({ offset: offsetDefault });
		}
	}, [state?.placement, update]);

	return { styles, attributes };
};

// TODO: Add hover event, currently can only be open via click
const MyPopover: React.FC<ITooltip> = ({
	children,
	className,
	icon,
	title,
	fieldname,
}) => {
	const analytics = useAnalytics();
	const { href } = useLocation();
	const [referenceElement, setReferenceElement] =
		useState<HTMLButtonElement | null>();
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>();
	const { styles, attributes } = usePopperCustomConfig(
		referenceElement,
		popperElement
	);
	const tag = fieldname || title || '';
	const getAnalytics = useCallback(() => {
		sendTooltipAnalytics(analytics, href, tag);
	}, [analytics, href, tag]);

	return (
		<Popover>
			<Popover.Button
				id={`${fieldname?.replace(/\s/g, '')}`}
				ref={setReferenceElement}
				className={className + ' flex'}
				onClick={getAnalytics}
				aria-labelledby={`${fieldname?.replace(/\s/g, '')}`}
				aria-describedby={`${fieldname?.replace(/\s/g, '')}-tooltip-content`}>
				{icon}
			</Popover.Button>
			<Popover.Panel
				ref={setPopperElement}
				style={styles.popper}
				className="z-50 tooltip-panel"
				{...attributes.popper}>
				<PopoverContent tabIndex={0} title={title} id={`${fieldname?.replace(/\s/g, '')}-tooltip-content`}>
					{children}
				</PopoverContent>
			</Popover.Panel>
		</Popover>
	);
};

export default MyPopover;
